<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { IonContent, IonIcon, IonSpinner, IonInput, IonButton } from '@ionic/vue';
import { useI18n } from 'vue-i18n';
import { checkmarkCircleOutline, mailOutline, receiptOutline, alertCircleOutline } from 'ionicons/icons';
import { DateTime } from 'luxon';

import TopBar from '@/shared/components/TopBar.vue';
import { useAssetsStore } from '@/stores/shared/assetsStore';
import { anykrowdApi } from '@/api/anykrowdApi';
import type { Order } from '@/shared/interfaces/wallet';

const { t } = useI18n();
const assetStore = useAssetsStore();
const route = useRoute();

const nanoid = route.params.nanoid as string;

const order = ref<Order | null>(null);
const isLoading = ref(true);
const hasError = ref(false);
const errorMessage = ref('');

const email = ref('');
const isSending = ref(false);
const emailSent = ref(false);
const emailError = ref('');

const formattedDate = computed(() => {
	if (!order.value?.created_at) return '';
	return DateTime.fromISO(order.value.created_at).toFormat('dd MMM yyyy, HH:mm');
});

const orderItems = computed(() => {
	if (!order.value?.overview?.items) return [];
	return order.value.overview.items;
});

const vatItems = computed(() => {
	if (!order.value?.overview?.vat_items) return [];
	return order.value.overview.vat_items;
});

const currencyPrefix = computed(() => {
	return order.value?.overview?.currency?.[0]?.prefix || order.value?.currency?.prefix || '€';
});

const company = computed(() => {
	return order.value?.company || null;
});

const hasCompanyDetails = computed(() => {
	if (!company.value) return false;
	return company.value.name || company.value.vat_id ||
		company.value.address?.street || company.value.address?.city;
});

const fetchOrder = async () => {
	try {
		isLoading.value = true;
		hasError.value = false;
		const { data } = await anykrowdApi.get<Order>(`/order/details/${nanoid}`, false);
		order.value = data;
	} catch (error: any) {
		hasError.value = true;
		errorMessage.value = error?.response?.data?.message || t('receipt.error_loading');
	} finally {
		isLoading.value = false;
	}
};

const sendEmail = async () => {
	if (!email.value || isSending.value) return;

	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	if (!emailRegex.test(email.value)) {
		emailError.value = t('receipt.invalid_email');
		return;
	}

	try {
		isSending.value = true;
		emailError.value = '';
		await anykrowdApi.post(`/order/${nanoid}/email-receipt`, { email: email.value }, false);
		emailSent.value = true;
	} catch (error: any) {
		emailError.value = error?.response?.data?.message || t('receipt.email_error');
	} finally {
		isSending.value = false;
	}
};

onMounted(() => {
	fetchOrder();
});
</script>

<template>
	<ion-content>
		<TopBar class="px-[34px]">
			<template #left>
				<div></div>
			</template>
			<template #center>
				<div class="flex justify-center w-full font-sourcesanspro-semibold text-l2">
					{{ t('receipt.title') }}
				</div>
			</template>
		</TopBar>

		<div class="px-[34px]">

			<!-- Loading State -->
			<div v-if="isLoading" class="flex flex-col items-center justify-center py-12">
				<ion-spinner name="crescent" class="w-12 h-12"></ion-spinner>
				<p class="mt-4 font-color-text-50">{{ t('receipt.loading') }}</p>
			</div>

			<!-- Error State -->
			<div v-else-if="hasError" class="flex flex-col items-center justify-center py-12">
				<ion-icon :icon="alertCircleOutline" class="w-16 h-16 font-color-error"></ion-icon>
				<p class="mt-4 font-color-text-50">{{ errorMessage }}</p>
			</div>

			<!-- Order Details -->
			<div v-else-if="order" class="space-y-6">
				<!-- Order Header -->
				<div class="background-white-15 rounded-xl p-4">
					<div class="flex items-center gap-3 mb-4">
						<ion-icon :icon="receiptOutline" class="w-8 h-8"></ion-icon>
						<div>
							<h2 class="font-sourcesanspro-semibold text-lg">{{ t('receipt.order_reference') }}</h2>
							<p class="font-color-text-50 text-sm uppercase">{{ order.nanoid }}</p>
						</div>
					</div>
					<div class="grid grid-cols-2 gap-4">
						<div>
							<p class="font-color-text-50 text-xs uppercase">{{ t('receipt.date') }}</p>
							<p class="text-sm">{{ formattedDate }}</p>
						</div>
						<div>
							<p class="font-color-text-50 text-xs uppercase">{{ t('receipt.total') }}</p>
							<p class="text-sm font-sourcesanspro-semibold">{{ currencyPrefix }}{{ order.price }}</p>
						</div>
					</div>
				</div>

				<!-- Order Items -->
				<div v-if="orderItems.length > 0" class="background-white-15 rounded-xl p-4">
					<h3 class="font-sourcesanspro-semibold mb-3">{{ t('receipt.items') }}</h3>
					<div class="space-y-2">
						<div v-for="(item, index) in orderItems" :key="index" class="flex justify-between items-center py-2 border-b border-white-15 last:border-0">
							<div class="flex items-center gap-2">
								<span class="font-color-text-50 text-sm">{{ item.quantity || 1 }}x</span>
								<span class="text-sm">{{ item.label }}</span>
							</div>
							<span class="text-sm">{{ currencyPrefix }}{{ item.price }}</span>
						</div>
					</div>
				</div>

				<!-- VAT Breakdown -->
				<div v-if="vatItems.length > 0" class="background-white-15 rounded-xl p-4">
					<h3 class="font-sourcesanspro-semibold mb-3">{{ t('receipt.vat_breakdown') }}</h3>
					<div class="space-y-2">
						<div v-for="(vatItem, index) in vatItems" :key="index" class="flex justify-between items-center py-2 border-b border-white-15 last:border-0">
							<span class="font-color-text-50 text-sm">{{ t('receipt.vat_rate') }} {{ vatItem.label }}</span>
							<span class="text-sm">{{ currencyPrefix }}{{ vatItem.price }}</span>
						</div>
					</div>
				</div>

				<!-- Email Section -->
				<div class="background-white-15 rounded-xl p-4">
					<div v-if="emailSent" class="flex flex-col items-center py-4">
						<ion-icon :icon="checkmarkCircleOutline" class="w-16 h-16 text-green-400"></ion-icon>
						<p class="mt-4 font-sourcesanspro-semibold">{{ t('receipt.email_sent') }}</p>
						<p class="font-color-text-50 text-sm text-center mt-2">{{ t('receipt.email_sent_description') }}</p>
					</div>
					<div v-else>
						<h3 class="font-sourcesanspro-semibold mb-3">{{ t('receipt.email_receipt') }}</h3>
						<div class="flex flex-col gap-3">
							<div class="relative">
								<ion-icon :icon="mailOutline" class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 font-color-text-50 z-10"></ion-icon>
								<ion-input
									v-model="email"
									type="email"
									:placeholder="t('receipt.email_placeholder')"
									class="background-white-15 rounded-lg pl-10"
									:class="{ 'border-error': emailError }"
								></ion-input>
							</div>
							<p v-if="emailError" class="font-color-error text-sm">{{ emailError }}</p>
							<ion-button expand="block" :disabled="!email || isSending" @click="sendEmail" class="mt-2">
								<ion-spinner v-if="isSending" name="crescent" class="w-5 h-5 mr-2"></ion-spinner>
								{{ t('receipt.send_email') }}
							</ion-button>
						</div>
					</div>
				</div>

				<!-- Company Details -->
				<div v-if="hasCompanyDetails" class="background-white-15 rounded-xl p-4">
					<div class="text-center space-y-1">
						<p v-if="company?.name" class="font-sourcesanspro-semibold">{{ company.name }}</p>
						<p v-if="company?.address?.street || company?.address?.number" class="font-color-text-50 text-sm">
							{{ company.address.street }} {{ company.address.number }}
						</p>
						<p v-if="company?.address?.postal_code || company?.address?.city" class="font-color-text-50 text-sm">
							{{ company.address.postal_code }} {{ company.address.city }}
						</p>
						<p v-if="company?.address?.country" class="font-color-text-50 text-sm">
							{{ company.address.country }}
						</p>
						<p v-if="company?.vat_id" class="font-color-text-50 text-sm">
							{{ t('receipt.vat_id') }}: {{ company.vat_id }}
						</p>
					</div>
				</div>
			</div>
		</div>
	</ion-content>
</template>

<style scoped>
ion-input {
	--padding-start: 2.5rem;
	--padding-end: 1rem;
	--padding-top: 0.75rem;
	--padding-bottom: 0.75rem;
	--color: var(--clientx-color-text);
	--placeholder-color: var(--clientx-color-text-50);
}

ion-button {
	--background-hover: var(--ion-color-primary-shade);
	--background-hover-opacity: 1;
}
</style>
